import {
  IconButton,
  SkeletonButton,
  Avatar,
  Button,
} from '@dev-spendesk/grapes';

import { QuerySuspense } from 'common/components/QuerySuspense';
import { fallbackSupplierLogoSrc } from 'common/components/SupplierLogo';
import { useTranslation } from 'common/hooks/useTranslation';
import type { QueryState } from 'src/core/api/queryState';
import { useCompanyCurrency } from 'src/core/modules/app/hooks/useCompanyCurrency';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { type WalletTransactionForecast } from 'src/core/modules/company/hooks';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

import { TransferListRowDueDate } from './TransferListRowDueDate';
import { isInvoiceBill } from '../../../../models';
import { useQuotes } from '../../../hooks';
import { useIsIwtEnabled } from '../../../hooks/api/useIsIwtEnabled';
import { type Transfer } from '../../../models';
import './TransferListRow.css';
import { InlineQuoteAmount } from '../../InlineQuoteAmount';

type Props = {
  transfer: Transfer;
  canApproveTransfers: boolean;
  onConfirmTransfer: (transfer: Transfer) => void;
  onCancelTransfer: (transfer: Transfer) => void;
  getWalletTransactionForecastQueryForTransfer: () => QueryState<
    WalletTransactionForecast,
    unknown
  >;
};

export const TransferListRow = ({
  transfer,
  canApproveTransfers,
  getWalletTransactionForecastQueryForTransfer,
  onConfirmTransfer,
  onCancelTransfer,
}: Props) => {
  const { t } = useTranslation('global');

  const { counterparty: counterParty } = transfer.bill;

  const companyCurrency = useCompanyCurrency();

  const companyId = useCompanyId();

  const walletTransactionForecastQueryState =
    getWalletTransactionForecastQueryForTransfer();
  const isIwtEnabled = useIsIwtEnabled();

  const { quotes } = useQuotes({
    selectedTransfers: [transfer],
    companyCurrency,
    companyId,
  });

  const handleConfirmTransfer = (): void => {
    if (
      walletTransactionForecastQueryState.status === 'success' &&
      walletTransactionForecastQueryState.data.hasEnoughFunds
    ) {
      return onConfirmTransfer(transfer);
    }

    // in case we could not assert if the wallet has enough funds or not, we don't block the confirm button and let the back end handle it
    if (walletTransactionForecastQueryState.status === 'error') {
      return onConfirmTransfer(transfer);
    }
  };

  const handleCancelTransfer = (): void => {
    return onCancelTransfer(transfer);
  };

  return (
    <div className="TransferListRow">
      <div className="TransferListRow__counter-party">
        <Avatar
          variant="square"
          src={counterParty.logo}
          fallbackSrc={fallbackSupplierLogoSrc}
          text={counterParty.name}
        />
        <div className="TransferListRow__counter-party__details">
          <div className="TransferListRow__counter-party__name body-m">
            {counterParty.name}
          </div>
          {isInvoiceBill(transfer.bill) &&
            transfer.bill.documentaryEvidence && (
              <div className="TransferListRow__counter-party__invoice-number body-s">
                #{' '}
                <span className="TransferListRow__counter-party__invoice-number--highlight">
                  {transfer.bill.documentaryEvidence.invoiceNumber}
                </span>
              </div>
            )}
          {!isInvoiceBill(transfer.bill) && (
            <div className="TransferListRow__counter-party__invoice-number body-s">
              <span className="TransferListRow__counter-party__invoice-number--highlight">
                {transfer.bill.counterparty.name}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-12">
        <div className="TransferListRow__details">
          <div className="TransferListRow__amount title-m flex items-center gap-4">
            {quotes.length && isIwtEnabled ? (
              <InlineQuoteAmount quote={quotes[0]} />
            ) : null}
            <p className="flex-1 self-end">
              {formatMonetaryValue(transfer.amount)}
            </p>
          </div>
          <TransferListRowDueDate transfer={transfer} />
        </div>

        <div
          className="flex items-center gap-12"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <IconButton
            onClick={handleCancelTransfer}
            aria-label="cancel row button"
            iconName="hexagone-cross"
            variant="secondaryAlert"
          />

          {canApproveTransfers ? (
            <QuerySuspense
              queryState={walletTransactionForecastQueryState}
              fallback={() => null}
              loading={<SkeletonButton />}
            >
              {({ hasEnoughFunds }) => (
                <Button
                  onClick={handleConfirmTransfer}
                  variant="secondaryNeutral"
                  isDisabled={!hasEnoughFunds}
                  text={t('invoices.transfer.actions.confirm')}
                />
              )}
            </QuerySuspense>
          ) : null}
        </div>
      </div>
    </div>
  );
};
